/**
 * AI Suggestions: knowledge-base gaps mined from real conversations.
 *
 * The table answers one question — what are visitors asking that this bot
 * cannot answer? — and every row is a proposal, never an applied change. The
 * drafted answers contain bracketed placeholders wherever the miner refused to
 * invent a fact, so the Add flow always opens an editor rather than a confirm
 * dialog: accepting an unread draft would train the bot to quote a placeholder
 * back to a customer.
 */

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api/client';
import type {
  Suggestion,
  SuggestionListResponse,
  SuggestionStatus,
} from '../api/types';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Modal,
  PageHeader,
  Spinner,
  StatusBadge,
} from '../components/ui';
import { formatDateTime } from '../lib/format';
import { useBots } from '../hooks/useBots';

type Tab = SuggestionStatus;

const TABS: { id: Tab; label: string }[] = [
  { id: 'pending', label: 'Pending review' },
  { id: 'added', label: 'Added to training' },
  { id: 'dismissed', label: 'Dismissed' },
];

/** Trim a long body to a readable preview without cutting mid-word. */
function preview(text: string, limit = 220): string {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const boundary = cut.lastIndexOf(' ');
  return `${boundary > limit / 2 ? cut.slice(0, boundary) : cut}…`;
}

export function Suggestions(): JSX.Element {
  const { selected } = useBots();
  const slug = selected?.slug ?? null;

  const [data, setData] = useState<SuggestionListResponse | null>(null);
  const [tab, setTab] = useState<Tab>('pending');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<Suggestion | null>(null);

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      setData(await api.listSuggestions(slug, tab));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load suggestions');
    } finally {
      setLoading(false);
    }
  }, [slug, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleScan(): Promise<void> {
    if (!slug) return;
    setScanning(true);
    setNotice(null);
    setError(null);
    try {
      const result = await api.generateSuggestions(slug);
      setNotice(
        result.errors.length > 0
          ? result.errors.join(' ')
          : `Reviewed ${result.examined} unanswered exchange${
              result.examined === 1 ? '' : 's'
            } · ${result.created} new suggestion${
              result.created === 1 ? '' : 's'
            }, ${result.updated} updated.`,
      );
      await load();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Could not scan conversations',
      );
    } finally {
      setScanning(false);
    }
  }

  async function handleDismiss(suggestion: Suggestion): Promise<void> {
    if (!slug) return;
    await api.dismissSuggestion(slug, suggestion.id);
    await load();
  }

  async function handleDelete(suggestion: Suggestion): Promise<void> {
    if (!slug) return;
    if (!window.confirm('Delete this suggestion? It may be proposed again later.'))
      return;
    await api.deleteSuggestion(slug, suggestion.id);
    await load();
  }

  if (!slug) return <EmptyState title="No bot selected" />;

  const pending = data?.pending_count ?? 0;

  return (
    <div>
      <PageHeader
        title="AI Suggestions"
        description="Help refine potential gaps in your data sources from real conversations"
        actions={
          <Button onClick={() => void handleScan()} disabled={scanning}>
            {scanning ? 'Scanning…' : 'Scan conversations'}
          </Button>
        }
      />

      {error && <ErrorNotice message={error} />}
      {notice && (
        <div className="mb-4 rounded-md border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
          {notice}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3">
          <div className="flex gap-1">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`border-b-2 px-3 py-2 text-sm ${
                  tab === item.id
                    ? 'border-brand-blue font-medium text-brand-blue'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {item.label}
                {item.id === 'pending' && pending > 0 && (
                  <span className="ml-1.5 rounded-full bg-brand-blue px-1.5 text-[11px] text-white">
                    {pending}
                  </span>
                )}
              </button>
            ))}
          </div>
          {pending > 0 && (
            <span className="py-2 text-sm text-slate-500">
              {pending} message{pending === 1 ? '' : 's'} pending review
            </span>
          )}
        </div>

        {loading ? (
          <Spinner />
        ) : !data || data.items.length === 0 ? (
          <EmptyState
            title="Nothing here yet"
            hint={
              tab === 'pending'
                ? 'Run a scan to look for questions the assistant could not answer.'
                : 'Suggestions you action appear under this tab.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">User Message</th>
                  <th className="px-4 py-2 font-medium">AI Response</th>
                  <th className="px-4 py-2 font-medium">Issue Type</th>
                  <th className="px-4 py-2 text-right font-medium">Seen</th>
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((suggestion) => (
                  <tr key={suggestion.id} className="align-top hover:bg-slate-50">
                    <td className="max-w-xs px-4 py-3">
                      <div className="font-medium text-slate-800">
                        {preview(suggestion.sample_message || suggestion.question, 160)}
                      </div>
                      {suggestion.sample_message &&
                        suggestion.question !== suggestion.sample_message && (
                          <div className="mt-1 text-xs text-slate-500">
                            Topic: {suggestion.question}
                          </div>
                        )}
                    </td>
                    <td className="max-w-md px-4 py-3 text-slate-600">
                      {preview(suggestion.ai_response) || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={suggestion.issue} />
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">
                      <div className="tabular-nums">{suggestion.occurrences}×</div>
                      <div className="text-xs text-slate-400">
                        {formatDateTime(suggestion.last_seen_at)}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      {suggestion.status === 'added' ? (
                        <span className="text-xs text-emerald-700">
                          Added
                          {suggestion.reviewed_by
                            ? ` by ${suggestion.reviewed_by}`
                            : ''}
                        </span>
                      ) : (
                        <div className="flex flex-col items-start gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing(suggestion)}
                            className="rounded px-2 py-1 text-xs text-brand-blue hover:bg-sky-50"
                          >
                            Review &amp; add
                          </button>
                          {suggestion.status !== 'dismissed' && (
                            <button
                              type="button"
                              onClick={() => void handleDismiss(suggestion)}
                              className="rounded px-2 py-1 text-xs text-slate-600 hover:bg-slate-100"
                            >
                              Dismiss
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => void handleDelete(suggestion)}
                            className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && slug && (
        <AcceptModal
          slug={slug}
          suggestion={editing}
          onClose={() => setEditing(null)}
          onAdded={() => {
            setEditing(null);
            setNotice(
              'Added to training. It will be searchable once embedding finishes.',
            );
            void load();
          }}
        />
      )}
    </div>
  );
}

/** Editor for the drafted answer, shown before anything is trained on. */
function AcceptModal({
  slug,
  suggestion,
  onClose,
  onAdded,
}: {
  slug: string;
  suggestion: Suggestion;
  onClose: () => void;
  onAdded: () => void;
}): JSX.Element {
  const [question, setQuestion] = useState(suggestion.question);
  const [answer, setAnswer] = useState(suggestion.suggested_answer);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The miner leaves these wherever it refused to invent a fact.
  const hasPlaceholder = /\[[^\]]+\]/.test(answer);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.acceptSuggestion(slug, suggestion.id, question.trim(), answer.trim());
      onAdded();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not add the answer');
    } finally {
      setBusy(false);
    }
  }

  const inputClass =
    'mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-blue';

  return (
    <Modal title="Review and add to training" onClose={onClose} wide>
      {error && <ErrorNotice message={error} />}

      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
        <p className="text-xs uppercase tracking-wide text-slate-400">
          What the visitor asked
        </p>
        <p className="mt-1 text-slate-800">{suggestion.sample_message || '—'}</p>
        <p className="mt-3 text-xs uppercase tracking-wide text-slate-400">
          What the assistant replied
        </p>
        <p className="mt-1 text-slate-600">{suggestion.ai_response || '—'}</p>
        {suggestion.reasoning && (
          <p className="mt-3 text-xs text-slate-500">{suggestion.reasoning}</p>
        )}
      </div>

      <div className="mt-4">
        <label className="text-sm font-medium text-slate-700" htmlFor="sug-question">
          Question
        </label>
        <input
          id="sug-question"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          className={inputClass}
        />
      </div>

      <div className="mt-4">
        <label className="text-sm font-medium text-slate-700" htmlFor="sug-answer">
          Answer
        </label>
        <textarea
          id="sug-answer"
          rows={10}
          value={answer}
          onChange={(event) => setAnswer(event.target.value)}
          className={inputClass}
        />
        <p className="mt-1 text-xs text-slate-500">
          Saved as a Q&amp;A data source, so the question and answer always retrieve
          together.
        </p>
      </div>

      {hasPlaceholder && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          This draft still contains a <code>[placeholder]</code>. The AI would not
          invent that detail — replace it with the real figure before adding.
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => void submit()}
          disabled={busy || !question.trim() || !answer.trim()}
        >
          {busy ? 'Adding…' : 'Add to training'}
        </Button>
      </div>
    </Modal>
  );
}
